import { useEffect, useMemo } from 'react'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useCustomersList } from '@/hooks/useCustomers'
import { usePoTemplate } from '@/hooks/usePurchaseOrders'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import {
  VENDOR_CATEGORIES,
  matchPartnerByName,
  partnerAddressLine,
  partnerHasAddress,
  sameShipTo,
  shipToFromPartner,
  shipToFromTemplate,
  vendorFromPartner,
} from '@/lib/poParties'
import type { PoDraft, PoDraftShipTo } from '@/lib/purchaseOrderDraft'

/** The template's default ship-to, as an option value no partner id can collide with. */
const TEMPLATE_SHIP_TO = '__template__'

/**
 * Pick the vendor from Partners (Vendor / Supplier) — z8nrz7f7dz. Choosing one
 * fills the vendor block; every field stays editable after. Hidden without
 * customers.read, whose route the list needs; the fields are typed instead.
 */
export function VendorPicker({ draft, onChange }: { draft: PoDraft; onChange: (next: PoDraft) => void }) {
  const { can } = usePermission()
  const allowed = can(PERMISSIONS.customersRead)
  const { data = [], isLoading } = useCustomersList({ enabled: allowed, categories: VENDOR_CATEGORIES })
  const options = useMemo(
    () =>
      [...data]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({ value: c.id, label: [c.name, partnerAddressLine(c)].filter(Boolean).join(' — ') })),
    [data],
  )
  const current = allowed ? matchPartnerByName(data, draft.supplierName) : null

  // A name that matches a partner (the material's supplier, typically) brings
  // that partner's details into whichever vendor fields are still blank.
  // Never overwrites: what the planner typed wins.
  useEffect(() => {
    if (!current) return
    const from = vendorFromPartner(current)
    const blanks = (Object.keys(from) as (keyof typeof from)[]).filter((k) => !draft[k].trim() && from[k])
    if (blanks.length > 0) onChange({ ...draft, ...Object.fromEntries(blanks.map((k) => [k, from[k]])) })
  }, [current?.id])

  if (!allowed) return null

  return (
    <div className='w-64'>
      <SearchableSelect
        value={current?.id ?? ''}
        onChange={(id) => {
          const p = data.find((c) => c.id === id)
          if (p) onChange({ ...draft, ...vendorFromPartner(p) })
        }}
        options={options}
        placeholder='Choose vendor…'
        emptyMessage={isLoading ? 'Loading vendors…' : 'No vendors yet. Add them under Partners.'}
        panelWidth={360}
      />
    </div>
  )
}

/**
 * Pick the ship-to from the template's default address and any partner with
 * an address (z8nrz7f7dz). Choosing one fills the ship-to block, still
 * editable. The partner list needs customers.read; the template default does not.
 */
export function ShipToPicker({ shipTo, onChange }: { shipTo: PoDraftShipTo; onChange: (next: PoDraftShipTo) => void }) {
  const { can } = usePermission()
  const allowed = can(PERMISSIONS.customersRead)
  const { data = [] } = useCustomersList({ enabled: allowed })
  const templateShipTo = shipToFromTemplate(usePoTemplate().data?.layout)
  const partners = useMemo(
    () => data.filter(partnerHasAddress).sort((a, b) => a.name.localeCompare(b.name)),
    [data],
  )

  const options = [
    ...(templateShipTo
      ? [{ value: TEMPLATE_SHIP_TO, label: `${templateShipTo.name || 'Our address'} — default` }]
      : []),
    ...partners.map((c) => ({
      value: c.id,
      label: `${c.name} (${c.category ?? 'Partner'}) — ${partnerAddressLine(c)}`,
    })),
  ]
  if (options.length === 0) return null

  const value =
    templateShipTo && sameShipTo(shipTo, templateShipTo)
      ? TEMPLATE_SHIP_TO
      : (partners.find((c) => sameShipTo(shipTo, shipToFromPartner(c)))?.id ?? '')

  return (
    <div className='w-64'>
      <SearchableSelect
        value={value}
        onChange={(id) => {
          if (id === TEMPLATE_SHIP_TO && templateShipTo) return onChange(templateShipTo)
          const p = partners.find((c) => c.id === id)
          if (p) onChange(shipToFromPartner(p))
        }}
        options={options}
        placeholder='Choose address…'
        panelWidth={400}
      />
    </div>
  )
}
